import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useHotkeys } from 'react-hotkeys-hook'
import { useSearchParams } from 'react-router-dom'
import FilterListIcon from '@material-ui/icons/FilterList'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import {
    AdminFilters,
    AdminFilterValues,
    AdminFiltersSlotRenderArgs,
    SelectFilterNames
} from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn, DataTableDeleteConfirmationCheckbox } from '../../Molecules/DataTable/DataTable'
import { displayError, displayErrorMessage } from '../../../../React/Common/Utils/Messages'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { deletePartitionMany, PartitionDeleteOutcome } from '../../../../NonReact/common/API/EntityAPIClient'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    PartitionModal,
    PartitionListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { ReportingAccessLevels } from '../../../../NonReact/constants/Permissions'
import { getPartitionList } from '../../../../React/Common/API/ItemsGetters'
import {
    PartitionList as PartitionRows,
    PartitionListItem,
    PartitionListResponse,
    PartitionListRow
} from '../../../../NonReact/Types/PartitionListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { COMMA, SPACE } from '../../../../NonReact/constants/Separators'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    ADMIN_URL_QUERY_PARTITION,
    ADMIN_URL_QUERY_PARTITION_GUID,
    ADMIN_URL_QUERY_TELEPHONY_SERVER,
    AdminListUrlBootstrap,
    buildAdminListPathWithQuery,
    parseAdminListUrlSearchParams
} from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialAdminScopeFilters } from '../../../Utils/adminListFilterReadiness'
import { PartitionConfigurationModal } from '../../../../React/PartitionConfiguration/Modal/PartitionConfigurationModal'
import { getPartitionListSyncTypeDisplayLabel } from '../../../../React/PartitionConfiguration/Utils/partitionListSyncTypeDisplay'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import {
    PARTITION_LIST_STATUS_FILTER_VALUES,
    PARTITION_LIST_SYNC_TYPE_FILTER_VALUES
} from './PartitionListFilterConstants'
import { AdminListCountLink } from '../../Atoms/AdminListCountLink/AdminListCountLink'
import { RouteID } from '../../Molecules/Header/Types/Types'
import { usePromiseValue } from '../../../../React/Common/Hooks/usePromiseValue'

/**
 * True when the filter value is a concrete TelSys selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony option value from admin filters.
 * @returns Whether the value should be sent to the list API as a restricting parameter.
 */
const isConcreteTelSysFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) {
        return false
    }

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

const ADMIN_FILTERS_SESSION_STORAGE_KEY: string = 'adminFilters.selectedScope'

/**
 * Reads persisted Telephony Server bootstrap value from admin filter session storage.
 *
 * @returns Stored Telephony Server value when present, otherwise undefined.
 */
const readPersistedTelephonyServerBootstrap = (): Definable<string> => {
    try {
        const rawState: Nullable<string> = window.sessionStorage.getItem(ADMIN_FILTERS_SESSION_STORAGE_KEY)

        if (!isStringNotEmpty(rawState ?? EMPTY_STRING)) {
            return undefined
        }

        const parsedState: unknown = JSON.parse(rawState as string)

        if (IsVarEmpty(parsedState) || typeof parsedState !== 'object') {
            return undefined
        }

        const telephonyServer: unknown = (parsedState as { telephonyServer?: unknown }).telephonyServer

        const telephonyServerStr: string =
            typeof telephonyServer === 'string' || typeof telephonyServer === 'number'
                ? String(telephonyServer)
                : EMPTY_STRING

        return isStringNotEmpty(telephonyServerStr) ? telephonyServerStr : undefined
    } catch {
        return undefined
    }
}

/**
 * Licence values a partition can actually hold, in the order the classic partition page lists them, each paired with
 * that page's own wording so the two read identically. The classic page's "Licensed for" combo offers exactly these
 * four; the remaining {@link ReportingAccessLevels} values are user-level licences and never sit on a partition.
 */
const PARTITION_LIST_LICENCE_FILTER_VALUES: ReadonlyArray<{
    readonly value: ReportingAccessLevels
    readonly labelKey: string
}> = [
    { value: ReportingAccessLevels.LITE, labelKey: PartitionListLanguage.PartitionList_LicenceFilter_AkixiLite },
    {
        value: ReportingAccessLevels.ONE_THOUSAND,
        labelKey: PartitionListLanguage.PartitionList_LicenceFilter_Akixi1000
    },
    {
        value: ReportingAccessLevels.TWO_THOUSAND,
        labelKey: PartitionListLanguage.PartitionList_LicenceFilter_Akixi2000
    },
    { value: ReportingAccessLevels.ENTERPRISE, labelKey: PartitionListLanguage.PartitionList_LicenceFilter_Business }
]

/**
 * Routed Partitions list view.
 *
 * @returns Partitions list page content.
 */
export const PartitionList: FC = (): ReactElement => {
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(PartitionListLanguage.NAME)
    language.loadBundle(PartitionModal.NAME)

    const {
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()

    const [searchParams] = useSearchParams()
    const urlBootstrap: Nullable<AdminListUrlBootstrap> = useMemo((): Nullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [persistedTelephonyServerBootstrap]: ReactState<Nullable<string>> = useState<Nullable<string>>(() =>
        readPersistedTelephonyServerBootstrap()
    )
    const [syncTypeFilter, setSyncTypeFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const [statusFilter, setStatusFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const [isDeletingPartitions, setIsDeletingPartitions]: ReactState<boolean> = useState<boolean>(false)
    const [isPartitionModalOpen, setIsPartitionModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isViewOnlyModal, setIsViewOnlyModal]: ReactState<boolean> = useState<boolean>(false)
    const [isCommonDevicesEditUnlocked, setIsCommonDevicesEditUnlocked]: ReactState<boolean> = useState<boolean>(false)
    const [selectedPartitionRow, setSelectedPartitionRow]: ReactState<Nullable<PartitionListRow>> =
        useState<Nullable<PartitionListRow>>(null)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const hasTelephonyServerBootstrap: boolean =
        isStringNotEmpty(urlBootstrap?.telephonyServer ?? EMPTY_STRING) ||
        isStringNotEmpty(persistedTelephonyServerBootstrap ?? EMPTY_STRING)
    const [isAwaitingInitialBootstrappedListResult, setIsAwaitingInitialBootstrappedListResult]: ReactState<boolean> =
        useState<boolean>(hasTelephonyServerBootstrap)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const cmeFilterValue: Definable<string | number> = filters?.cme?.value
    const listCmeId: Nullable<string> = IsVarEmpty(cmeFilterValue)
        ? userInfo?.Scope?.CMEnvironmentID ?? null
        : String(cmeFilterValue)
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const telSysFilterValue: Definable<string | number> = filters?.telephonyServer?.value
    const listTelSysId: Definable<string | number> = isConcreteTelSysFilterValue(telSysFilterValue)
        ? telSysFilterValue
        : undefined
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listLicenceFilter: Definable<string> =
        isVarNotEmpty(filters?.licenceType?.value) && filters.licenceType.value !== ALL_VALUE
            ? String(filters.licenceType.value)
            : undefined
    const listSyncTypeFilter: Definable<string> =
        isVarNotEmpty(syncTypeFilter?.value) && syncTypeFilter.value !== ALL_VALUE
            ? String(syncTypeFilter.value)
            : undefined
    const listStatusFilter: Definable<string> =
        isVarNotEmpty(statusFilter?.value) && statusFilter.value !== ALL_VALUE ? String(statusFilter.value) : undefined

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_PARTITIONS, filters, userInfo?.Scope?.CMEnvironmentID)

    const shouldRequestPartitions: boolean =
        !isWaitingForInitialFiltersLoad &&
        !isBootstrappingFromUrl &&
        !IsVarEmpty(listCmeId) &&
        !listNoTelephonyServer &&
        isVarNotEmpty(listTelSysId)
    const cmeIdForCountLinks: string = String(listCmeId ?? userInfo?.Scope?.CMEnvironmentID ?? EMPTY_STRING)
    const [currentPage, rowsPerPage]: PaginationInfo = pagination
    const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch
    const fetchPartitionList = useCallback(
        (): Promise<Nullable<PartitionListResponse>> =>
            getPartitionList(
                listTelSysId as string | number,
                currentPage * rowsPerPage,
                rowsPerPage,
                sort,
                searchQuery,
                undefined,
                listLicenceFilter,
                listSyncTypeFilter,
                listStatusFilter
            ),
        [
            currentPage,
            listLicenceFilter,
            listStatusFilter,
            listSyncTypeFilter,
            listTelSysId,
            rowsPerPage,
            searchQuery,
            sort
        ]
    )
    const {
        value: partitionListResponse,
        loading: partitionListLoading,
        error: partitionListError,
        refresh: refreshPartitionList
    } = usePromiseValue<Nullable<PartitionListResponse>>(
        fetchPartitionList,
        {
            initialValue: null,
            enabled: shouldRequestPartitions
        },
        [
            currentPage,
            rowsPerPage,
            sort,
            searchQuery,
            listLicenceFilter,
            listSyncTypeFilter,
            listStatusFilter,
            listTelSysId,
            shouldRequestPartitions
        ]
    )
    /**
     * True after filters/session hydration when a list request should run but {@link usePromiseValue} has not yet
     * flipped `loading` (or returned the first payload). Keeps the table skeleton on without a flash of empty rows.
     */
    const isPartitionListAwaitingFirstResult: boolean =
        shouldRequestPartitions &&
        !partitionListLoading &&
        IsVarEmpty(partitionListError) &&
        IsVarEmpty(partitionListResponse)
    const partitionList: PartitionRows = useMemo(
        (): PartitionRows =>
            partitionListResponse?.Data?.Items?.map(
                (item: PartitionListItem, index: number): PartitionListRow => ({
                    key: item.PartIDInTelSys ?? `partition-row-${index}`,
                    TelSysID: listTelSysId as string | number,
                    PartitionID: item.PartitionGUID ?? EMPTY_STRING,
                    Description: item.Description ?? EMPTY_STRING,
                    PartIDInTelSys: item.PartIDInTelSys ?? EMPTY_STRING,
                    LicensedForRptUserType: item.LicensedForRptUserType ?? EMPTY_STRING,
                    ExtCount: item.ExtCount ?? EMPTY_STRING,
                    UserCount: item.UserCount ?? EMPTY_STRING,
                    AcdAgentCount: item.AcdAgentCount ?? EMPTY_STRING,
                    SyncType: item.SyncType ?? EMPTY_STRING,
                    CommsStatus: item.CommsStatus ?? EMPTY_STRING
                })
            ) ?? [],
        [listTelSysId, partitionListResponse]
    )
    const commonDevicesPartitionIds: Set<string> = useMemo(
        (): Set<string> =>
            new Set<string>(
                partitionListResponse?.Data?.Items?.filter(
                    (item: PartitionListItem): boolean => item.IsGlobal === true
                ).map((item: PartitionListItem): string => String(item.PartIDInTelSys ?? EMPTY_STRING)) ?? []
            ),
        [partitionListResponse]
    )
    const canEditPartitionRow = useCallback(
        (row: Definable<PartitionListRow>): boolean => {
            if (IsVarEmpty(row)) {
                return false
            }

            return isCommonDevicesEditUnlocked || !commonDevicesPartitionIds.has(String(row.PartIDInTelSys))
        },
        [commonDevicesPartitionIds, isCommonDevicesEditUnlocked]
    )

    const resetPaginationToFirstPage = useCallback((): void => {
        setPagination(([, currentRowsPerPage]: PaginationInfo): PaginationInfo => [0, currentRowsPerPage])
    }, [setPagination])

    // Changing a filter has to go back to page one: the narrowed result may have fewer pages than the offset
    // being held, which would ask the server for a page past the end and show an empty table.
    const changeSyncTypeFilter = useCallback(
        (nextFilter: Nullable<SelectFilterOption>): void => {
            setSyncTypeFilter(nextFilter)
            resetPaginationToFirstPage()
        },
        [resetPaginationToFirstPage, setSyncTypeFilter]
    )

    const changeStatusFilter = useCallback(
        (nextFilter: Nullable<SelectFilterOption>): void => {
            setStatusFilter(nextFilter)
            resetPaginationToFirstPage()
        },
        [resetPaginationToFirstPage, setStatusFilter]
    )

    const licenceFilterOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            {
                value: ALL_VALUE,
                label: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_ANY_LABEL)
            },
            ...PARTITION_LIST_LICENCE_FILTER_VALUES.map(
                (licence: { value: ReportingAccessLevels; labelKey: string }): SelectFilterOption => ({
                    value: licence.value,
                    label: language.get(licence.labelKey)
                })
            )
        ],
        [language]
    )

    const syncTypeFilterOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            {
                value: ALL_VALUE,
                label: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_ANY_LABEL)
            },
            ...PARTITION_LIST_SYNC_TYPE_FILTER_VALUES.map(
                (syncType: string): SelectFilterOption => ({
                    value: syncType,
                    label: getPartitionListSyncTypeDisplayLabel(language, syncType)
                })
            )
        ],
        [language]
    )

    // The Status column renders the API's own status string, so the filter offers exactly those values and the
    // two always read the same.
    const statusFilterOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            {
                value: ALL_VALUE,
                label: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_ANY_LABEL)
            },
            ...PARTITION_LIST_STATUS_FILTER_VALUES.map(
                (status: string): SelectFilterOption => ({ value: status, label: status })
            )
        ],
        [language]
    )

    const getPartitions = useCallback((): void => {
        if (!shouldRequestPartitions) {
            setTotalCount(0)
            setHasResolvedInitialUrlBootstrapLoad(true)
            return
        }

        refreshPartitionList().catch((): void => undefined)
    }, [refreshPartitionList, setTotalCount, shouldRequestPartitions])

    const describePartitionRow = useCallback(
        (row: PartitionListRow): string =>
            isStringNotEmpty(row.Description) ? row.Description : String(row.PartIDInTelSys),
        []
    )

    const countApplicationUsers = useCallback(
        (selectedRows: PartitionListRow[]): number =>
            selectedRows.reduce(
                (runningTotal: number, row: PartitionListRow): number => runningTotal + (Number(row.UserCount) || 0),
                0
            ),
        []
    )

    // Deleting a partition takes its devices, ACD agents and application users with it, so the prompt has to
    // say so. The classic list discloses the same consequences before it deletes anything.
    const buildDeleteConfirmationMessage = useCallback(
        (selectedRows: PartitionListRow[]): string => {
            const message: string = language.getInterpolated(
                PartitionListLanguage.PartitionList_Delete_Confirm,
                selectedRows.map(describePartitionRow).join(`${COMMA}${SPACE}`)
            )

            const totalUserCount: number = countApplicationUsers(selectedRows)

            if (totalUserCount <= 0) {
                return message
            }

            return `${message}${language.getInterpolated(
                PartitionListLanguage.PartitionList_Delete_Confirm_AppUsers,
                String(totalUserCount)
            )}`
        },
        [countApplicationUsers, describePartitionRow, language]
    )

    // Only worth offering when there are users to keep. The classic list ticks it by default, so deleting them
    // stays the unchanged behaviour and preserving them is the deliberate choice.
    const buildDeleteConfirmationCheckbox = useCallback(
        (selectedRows: PartitionListRow[]): Definable<DataTableDeleteConfirmationCheckbox> =>
            countApplicationUsers(selectedRows) > 0
                ? {
                      label: language.get(PartitionListLanguage.PartitionList_Delete_Checkbox_AutoDeleteUsers),
                      defaultChecked: true
                  }
                : undefined,
        [countApplicationUsers, language]
    )

    const canDeletePartitionSelection = useCallback(
        (selectedRows: PartitionListRow[]): boolean => selectedRows.every(canEditPartitionRow),
        [canEditPartitionRow]
    )

    const deleteSelectedPartitions = useCallback(
        (selectedRows: PartitionListRow[], isAutoDeleteUsersChecked?: boolean): void => {
            if (ArrayUtils.isEmpty(selectedRows)) return

            // A global partition is read-only on a hosted telephony server: Change already refuses to open one
            // here and the classic list disables Delete while one is selected, so the whole selection is
            // refused rather than partly deleted.
            const readOnlyRows: PartitionListRow[] = selectedRows.filter(
                (row: PartitionListRow): boolean => !canEditPartitionRow(row)
            )

            if (!ArrayUtils.isEmpty(readOnlyRows)) {
                displayErrorMessage(
                    language.getInterpolated(
                        PartitionListLanguage.PartitionList_Delete_ReadOnlyRefused,
                        readOnlyRows.map(describePartitionRow).join(`${COMMA}${SPACE}`)
                    )
                )
                return
            }

            setIsDeletingPartitions(true)

            // Every selected row belongs to the telephony server the list is filtered to, so one telSys ID
            // covers the whole selection. The server rejects a partition it will not delete (for example while
            // its telephone module is in service), and because the others still go through, the failures are
            // named individually and the list is refreshed either way.
            const descriptionsByPartitionID: Map<string, string> = new Map<string, string>(
                selectedRows.map((row: PartitionListRow): [string, string] => [
                    String(row.PartIDInTelSys),
                    describePartitionRow(row)
                ])
            )

            // The prompt only carries the checkbox when the selection has application users, so no checkbox
            // means there are none to preserve and the server default applies.
            const isAutoDeleteUsers: boolean = isAutoDeleteUsersChecked ?? true

            deletePartitionMany(
                String(selectedRows[0].TelSysID),
                selectedRows.map((row: PartitionListRow): string => String(row.PartIDInTelSys)),
                isAutoDeleteUsers
            )
                .then((outcomes: PartitionDeleteOutcome[]): void => {
                    const failures: PartitionDeleteOutcome[] = outcomes.filter(
                        (outcome: PartitionDeleteOutcome): boolean => !outcome.isDeleted
                    )

                    if (ArrayUtils.isEmpty(failures)) return

                    displayErrorMessage(
                        language.getInterpolated(
                            PartitionListLanguage.PartitionList_Delete_Failures,
                            failures
                                .map((outcome: PartitionDeleteOutcome): string => {
                                    const description: string =
                                        descriptionsByPartitionID.get(outcome.partitionID) ?? outcome.partitionID

                                    return isStringNotEmpty(outcome.error?.message)
                                        ? `${description}${COMMA}${SPACE}${outcome.error?.message}`
                                        : description
                                })
                                .join('\n')
                        )
                    )
                })
                .catch((error: Error): void => {
                    displayError(error)
                })
                .finally((): void => {
                    // Rows that were deleted have to leave the table even when a sibling delete failed.
                    getPartitions()
                    if (isMounted.current) setIsDeletingPartitions(false)
                })
        },
        [canEditPartitionRow, describePartitionRow, getPartitions, isMounted, language]
    )

    useEffect((): void => {
        if (IsVarEmpty(partitionListError)) {
            return
        }

        displayError(partitionListError)
    }, [partitionListError])

    useEffect((): void => {
        if (!shouldRequestPartitions) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            return
        }

        if (partitionListLoading) return

        setTotalCount(Number(partitionListResponse?.Data?.Total) || 0)
        setHasResolvedInitialUrlBootstrapLoad(true)
    }, [partitionListLoading, partitionListResponse, setTotalCount, shouldRequestPartitions])

    useEffect((): void => {
        if (!isAwaitingInitialBootstrappedListResult) return

        if (listNoTelephonyServer) {
            setIsAwaitingInitialBootstrappedListResult(false)
            return
        }

        if (isVarNotEmpty(partitionListResponse) || isVarNotEmpty(partitionListError)) {
            setIsAwaitingInitialBootstrappedListResult(false)
        }
    }, [isAwaitingInitialBootstrappedListResult, listNoTelephonyServer, partitionListError, partitionListResponse])

    const columns: DataTableColumn<PartitionListRow, keyof PartitionListRow>[] = useMemo(
        (): DataTableColumn<PartitionListRow, keyof PartitionListRow>[] => [
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_IDInTelSys),
                field: 'PartIDInTelSys',
                key: 'partition-list-part-id',
                sortable: true
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Description),
                field: 'Description',
                key: 'partition-list-description',
                sortable: true,
                align: 'left',
                renderCell: (row: PartitionListRow): ReactNode =>
                    canEditPartitionRow(row) ? (
                        <span
                            className='router-datatable-body-clickable partition-list-description-clickable'
                            onMouseDown={(event: React.MouseEvent): void => {
                                event.preventDefault()
                                setSelectedPartitionRow(row)
                                setIsViewOnlyModal(false)
                                setIsPartitionModalOpen(true)
                            }}
                        >
                            {row.Description}
                        </span>
                    ) : (
                        row.Description
                    )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Licence),
                field: 'LicensedForRptUserType',
                key: 'partition-list-licence',
                sortable: true
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Extensions),
                field: 'ExtCount',
                key: 'partition-list-ext',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ExtCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.DEVICE}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(row.PartIDInTelSys),
                            [ADMIN_URL_QUERY_PARTITION_GUID]: String(row.PartitionID),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_ExtensionsAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_AcdAgents),
                field: 'AcdAgentCount',
                key: 'partition-list-agents',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.AcdAgentCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.AGENT}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(row.PartIDInTelSys),
                            [ADMIN_URL_QUERY_PARTITION_GUID]: String(row.PartitionID),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_AcdAgentsAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_AppUsers),
                field: 'UserCount',
                key: 'partition-list-users',
                sortable: false,
                disableRowSelection: true,
                renderCell: (row: PartitionListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.UserCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.USERS}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(row.PartIDInTelSys),
                            [ADMIN_URL_QUERY_PARTITION_GUID]: String(row.PartitionID),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(PartitionListLanguage.PartitionList_CountLink_AppUsersAria)}
                    />
                )
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_SyncType),
                field: 'SyncType',
                key: 'partition-list-sync',
                sortable: true,
                renderCell: (row: PartitionListRow): ReactNode =>
                    getPartitionListSyncTypeDisplayLabel(language, row.SyncType)
            },
            {
                headerName: language.get(PartitionListLanguage.PartitionList_Column_Status),
                field: 'CommsStatus',
                key: 'partition-list-status',
                sortable: true
            }
        ],
        [canEditPartitionRow, cmeIdForCountLinks, language, listIncludeRecursive]
    )

    const openAddModal = useCallback((): void => {
        setSelectedPartitionRow(null)
        setIsViewOnlyModal(false)
        setIsPartitionModalOpen(true)
    }, [])

    const openChangeModal = useCallback(
        (selectedRows: PartitionListRow[]): void => {
            const selectedRow: Nullable<PartitionListRow> = selectedRows[0] ?? null
            const isCommonDevicesPartition: boolean =
                isVarNotEmpty(selectedRow) && commonDevicesPartitionIds.has(String(selectedRow.PartIDInTelSys))
            if (isCommonDevicesPartition && !isCommonDevicesEditUnlocked) return

            setSelectedPartitionRow(selectedRow)
            setIsViewOnlyModal(false)
            setIsPartitionModalOpen(true)
        },
        [commonDevicesPartitionIds, isCommonDevicesEditUnlocked]
    )

    const openViewModal = useCallback((selectedRows: PartitionListRow[]): void => {
        setSelectedPartitionRow(selectedRows[0] ?? null)
        setIsViewOnlyModal(true)
        setIsPartitionModalOpen(true)
    }, [])

    const closePartitionModal = useCallback((): void => {
        setIsPartitionModalOpen(false)
        setSelectedPartitionRow(null)
        setIsViewOnlyModal(false)
    }, [])

    const previousUrlBootstrapKeyRef: MutableRefObject<string> = useRef<string>(EMPTY_STRING)

    useEffect((): void => {
        const nextKey: string = IsVarEmpty(urlBootstrap) ? EMPTY_STRING : JSON.stringify(urlBootstrap)

        if (nextKey === previousUrlBootstrapKeyRef.current) return

        previousUrlBootstrapKeyRef.current = nextKey

        if (IsVarEmpty(urlBootstrap)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
        } else {
            setHasResolvedInitialUrlBootstrapLoad(false)
        }
    }, [urlBootstrap])

    useHotkeys(
        'ctrl+alt+/',
        (event: KeyboardEvent): void => {
            event.preventDefault()
            setIsCommonDevicesEditUnlocked(true)
        },
        { enableOnFormTags: true },
        []
    )

    return (
        <div className='partition-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_partitions} />
            <div className='partition-list-table-container admin-table-list-container'>
                <AdminFilters
                    labelTelSysPartitionAllSentinelAsNone
                    selectFilterPlaceholderTooltips={{
                        [SelectFilterNames.TELEPHONY_SERVER]: true
                    }}
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)
                        if (hasResolvedInitialAdminScopeFilters(values)) {
                            setHasResolvedInitialFiltersLoad(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    additionalFilterSkeletonCount={3}
                    additionalFilters={({
                        isLoading,
                        filterValues,
                        onSelectFilterChange
                    }: AdminFiltersSlotRenderArgs): ReactElement => ( // NOSONAR
                        <>
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        onSelectFilterChange(SelectFilterNames.LICENCE_TYPE, value)
                                    }}
                                    options={licenceFilterOptions}
                                    label={language.get(PartitionListLanguage.PartitionList_Filter_Licence)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={filterValues.licenceType ?? null}
                                    defaultValue={licenceFilterOptions[0]?.value}
                                />
                            </div>
                            <div>
                                <SelectFilter
                                    onChange={changeSyncTypeFilter}
                                    options={syncTypeFilterOptions}
                                    label={language.get(PartitionListLanguage.PartitionList_Column_SyncType)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={syncTypeFilter}
                                    defaultValue={syncTypeFilterOptions[0]?.value}
                                />
                            </div>
                            <div>
                                <SelectFilter
                                    onChange={changeStatusFilter}
                                    options={statusFilterOptions}
                                    label={language.get(PartitionListLanguage.PartitionList_Column_Status)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={statusFilter}
                                    defaultValue={statusFilterOptions[0]?.value}
                                />
                            </div>
                        </>
                    )}
                    onRefresh={getPartitions}
                />
                <DataTable<PartitionListRow, keyof PartitionListRow>
                    permissionName={AdminBackendPermissionNames.PARTITION}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={partitionList}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={
                        partitionListLoading ||
                        isDeletingPartitions ||
                        isWaitingForInitialUrlBootstrapLoad ||
                        isWaitingForInitialFiltersLoad ||
                        isAwaitingInitialBootstrappedListResult ||
                        isPartitionListAwaitingFirstResult
                    }
                    paginationServer
                    sortServer
                    isSelectable
                    onSortChange={setSort}
                    toolbarAddDisabled={IsVarEmpty(listTelSysId)}
                    toolbarAddDisabledTitle={language.get(
                        PartitionListLanguage.PartitionList_Add_Disabled_SelectTelephonyServer
                    )}
                    onToolbarAddClick={openAddModal}
                    onToolbarChangeClick={openChangeModal}
                    onToolbarViewClick={openViewModal}
                    getDeleteConfirmationMessage={buildDeleteConfirmationMessage}
                    getDeleteConfirmationCheckbox={buildDeleteConfirmationCheckbox}
                    canDeleteSelection={canDeletePartitionSelection}
                    toolbarDeleteDisabledTitle={language.get(
                        PartitionListLanguage.PartitionList_Delete_Disabled_ReadOnly
                    )}
                    onToolbarDeleteClick={deleteSelectedPartitions}
                    onRowsPerPageChange={(nextPage: number, nextRowsPerPage: number): void =>
                        setPagination([nextPage, nextRowsPerPage])
                    }
                    onPageChange={(nextPage: number, nextRowsPerPage: number): void =>
                        setPagination([nextPage, nextRowsPerPage])
                    }
                />
                {isPartitionModalOpen && isVarNotEmpty(listTelSysId) ? (
                    <PartitionConfigurationModal
                        key={`partition-modal-${selectedPartitionRow?.PartIDInTelSys ?? 'add'}-${
                            isViewOnlyModal ? 'view' : 'edit'
                        }`}
                        refreshAction={getPartitions}
                        filterTelSysID={Number(selectedPartitionRow?.TelSysID ?? listTelSysId)}
                        selectedPartitionIDInTelSys={
                            isVarNotEmpty(selectedPartitionRow?.PartIDInTelSys)
                                ? Number(selectedPartitionRow.PartIDInTelSys)
                                : undefined
                        }
                        viewOnly={isViewOnlyModal}
                        onModalExited={closePartitionModal}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
